import { SECTION_DESCRIPTIONS } from '@communities/community/utils/sectionDescriptions';
import { Text, Heading, Dialog } from '@soteria/design-system-ve';

import * as styles from './MoreInfoDialog.css';

interface LabelsMoreInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LabelsMoreInfoDialog({ isOpen, onClose }: LabelsMoreInfoDialogProps) {
  return (
    <Dialog cancelAction={{ label: 'Close' }} isOpen={isOpen} size="xxxl" title={SECTION_DESCRIPTIONS.STANDARD_LABELS.title} onClose={onClose}>
      <div className={styles.dialogContent}>
        <Text>
          Standard labels are predefined content classification categories that help organize and manage content across your communities by providing a
          consistent way to categorize and identify different types of content for moderation, reporting, and automated processing.
        </Text>

        <div className={styles.section}>
          <Heading level="h3">How Standard Labels Work:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>
                <strong>Content Classification</strong> - Labels define specific content categories with clear policies and guidelines
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Unique Identification</strong> - Each label has a unique identifier for consistent reference across systems
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Scoped Access</strong> - Labels can be globally available or restricted to specific community groups
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Automated Processing</strong> - Content can be automatically or manually tagged during moderation workflows
              </Text>
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <Heading level="h3">Key Features:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>
                <strong>Standardized Categories</strong> - Ensures uniform content handling and reporting across all communities
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Approval Workflow</strong> - New labels start as "pending" and require system administrator approval
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Policy Guidelines</strong> - Each label includes detailed descriptions to guide proper content classification
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Access Control</strong> - Users only see labels that are globally available or scoped to their community groups
              </Text>
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <Heading level="h3">Label Status:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>
                <strong>Pending</strong> - Newly created labels awaiting system administrator approval before becoming active
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Approved</strong> - Active labels ready for use in content classification and moderation workflows
              </Text>
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <Heading level="h3">Benefits:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>
                <strong>Consistency</strong> - Uniform content categorization across all your communities
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Automation</strong> - Enables automated content processing and routing based on classification
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Reporting</strong> - Standardized labels improve content analytics and reporting capabilities
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Moderation</strong> - Helps both automated systems and human moderators classify content properly
              </Text>
            </li>
          </ul>
        </div>

        <Text>Standard labels are essential for maintaining organized, efficient content management and moderation across your community ecosystem.</Text>
      </div>
    </Dialog>
  );
}
